import { TREND_AXIS, type TrendOverlay, type TrendSeries } from '@bcecloud/portal-ui';
import type { LeadStats } from './leads';

/**
 * Leads over time, "Signed" view: per bucket, the leads and the opportunities
 * created without a lead as two bars side by side, each split into what
 * signed and what did not, so where signed work came from reads at a glance.
 * Close rates (signed so far ÷ created in the bucket) can ride on a
 * right-hand percent axis. Pure; the page passes the stats and its toggles.
 */

export const SIGNED_VIEW_LABELS = {
  leadsSigned: 'Leads · signed',
  leadsNotSigned: 'Leads · not signed',
  withoutLeadSigned: 'Without a lead · signed',
  withoutLeadNotSigned: 'Without a lead · not signed',
  leadsRate: 'Leads close rate',
  withoutLeadRate: 'Without a lead close rate',
} as const;

const STACK = { leads: 'leads', withoutLead: 'without-lead' } as const;
/** SERIES_COLORS slots: brand for leads, orange for opportunities without a lead. */
const COLOR_SLOT = { leads: 0, withoutLead: 1 } as const;
/** Overlay strokes: solid for leads, dashed for without a lead, so the pair differs by more than hue. */
const RATE_TONE = { leads: 0, withoutLead: 1 } as const;

const PERCENT = 100;
const TENTHS = 10;

/** Signed as a percent of created, to one decimal; null (a gap) when nothing was created. */
export function closeRate(signed: number, created: number): number | null {
  return created === 0 ? null : Math.round((signed / created) * PERCENT * TENTHS) / TENTHS;
}

export interface SignedView {
  readonly series: TrendSeries[];
  readonly overlays: TrendOverlay[];
  /** Tooltip lines per visible bucket, in point order. */
  readonly details: string[][];
  /** Whether the opportunities without a lead are drawn (the API sent per-bucket signed counts and the toggle is on). */
  readonly withoutLeadShown: boolean;
}

function summary(created: number, signed: number, noun: string): string {
  const rate = closeRate(signed, created);
  return `${created.toLocaleString()} ${noun} · ${signed.toLocaleString()} signed${rate === null ? '' : ` (${rate}%)`}`;
}

/**
 * @param stats - GET /leads/stats; its series and withoutLead carry the lead-in buckets, dropped here.
 * @param options.showWithoutLead - Draw the without-a-lead bars beside the leads.
 * @param options.showCloseRate - Add a close-rate line per bar group on the secondary axis.
 */
export function buildSignedView(
  stats: Pick<LeadStats, 'range' | 'series' | 'withoutLead'>,
  options: { readonly showWithoutLead: boolean; readonly showCloseRate: boolean },
): SignedView {
  const leadIn = stats.range.leadIn;
  const points = stats.series.slice(leadIn);
  const leads = points.map((point) => point.leads);
  const leadsSigned = points.map((point) => point.signed);

  const direct = stats.withoutLead;
  const withoutLead =
    options.showWithoutLead && direct?.signedSeries
      ? { created: direct.series.slice(leadIn), signed: direct.signedSeries.slice(leadIn) }
      : null;

  const series: TrendSeries[] = [
    { key: 'leads-signed', label: SIGNED_VIEW_LABELS.leadsSigned, values: leadsSigned, stack: STACK.leads, colorIndex: COLOR_SLOT.leads },
    {
      key: 'leads-not-signed',
      label: SIGNED_VIEW_LABELS.leadsNotSigned,
      values: leads.map((count, index) => count - (leadsSigned[index] ?? 0)),
      stack: STACK.leads,
      colorIndex: COLOR_SLOT.leads,
      faded: true,
    },
  ];
  const overlays: TrendOverlay[] = options.showCloseRate
    ? [
        {
          key: 'leads-rate',
          label: SIGNED_VIEW_LABELS.leadsRate,
          values: leads.map((count, index) => closeRate(leadsSigned[index] ?? 0, count)),
          tone: RATE_TONE.leads,
          colorIndex: COLOR_SLOT.leads,
          axis: TREND_AXIS.secondary,
        },
      ]
    : [];

  if (withoutLead) {
    series.push(
      {
        key: 'without-lead-signed',
        label: SIGNED_VIEW_LABELS.withoutLeadSigned,
        values: [...withoutLead.signed],
        stack: STACK.withoutLead,
        colorIndex: COLOR_SLOT.withoutLead,
      },
      {
        key: 'without-lead-not-signed',
        label: SIGNED_VIEW_LABELS.withoutLeadNotSigned,
        values: withoutLead.created.map((count, index) => count - (withoutLead.signed[index] ?? 0)),
        stack: STACK.withoutLead,
        colorIndex: COLOR_SLOT.withoutLead,
        faded: true,
      },
    );
    if (options.showCloseRate) {
      overlays.push({
        key: 'without-lead-rate',
        label: SIGNED_VIEW_LABELS.withoutLeadRate,
        values: withoutLead.created.map((count, index) => closeRate(withoutLead.signed[index] ?? 0, count)),
        tone: RATE_TONE.withoutLead,
        colorIndex: COLOR_SLOT.withoutLead,
        axis: TREND_AXIS.secondary,
      });
    }
  }

  const details = points.map((point, index) => [
    summary(point.leads, point.signed, 'leads'),
    ...(withoutLead
      ? [summary(withoutLead.created[index] ?? 0, withoutLead.signed[index] ?? 0, 'opportunities without a lead')]
      : []),
  ]);

  return { series, overlays, details, withoutLeadShown: withoutLead !== null };
}
